// CRM Mailbox connect + bulk modals — rehouses the classic Mailboxes connect surface
// into the Deliverability shell: two-mode SMTP (single with test-before-save + bulk CSV
// import with ManyReach column mapping), Zoho Shared (parent send-as), and Bulk edit
// (caps + warmup). Client-scoped: new SMTP/shared mailboxes are created then assignable
// to this client's pools. CRM styling; orange is action-only.
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import API_BASE from '../../config';

// ── SMTP modal: single (test → save) + bulk CSV import ────────────────────
export function SmtpConnectModal({ onClose, onImported }) {
  const { getToken } = useAuth();
  const [mode, setMode] = useState('single');

  const [emailAddress, setEmailAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapUsername, setImapUsername] = useState('');
  const [imapPassword, setImapPassword] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const [bulkRows, setBulkRows] = useState([]);
  const [bulkParseError, setBulkParseError] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const testBody = () => ({
    smtp_host: smtpHost, smtp_port: parseInt(smtpPort) || 587,
    smtp_username: smtpUsername || emailAddress, smtp_password: smtpPassword,
    imap_host: imapHost || null, imap_port: parseInt(imapPort) || 993,
    imap_username: imapUsername || emailAddress, imap_password: imapPassword || smtpPassword,
  });

  async function runTest() {
    setTesting(true); setTestResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(testBody()),
      });
      setTestResult(await res.json());
    } catch (e) { setTestResult({ ok: false, errors: { network: e.message } }); }
    finally { setTesting(false); }
  }

  async function saveSingle() {
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email_address: emailAddress, display_name: displayName || null, ...testBody() }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error('Save failed: ' + (data.error || res.status)); return; }
      toast.success('Mailbox added');
      onImported();
    } catch (e) { toast.error('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  function handleCsvFile(file) {
    setBulkParseError(''); setBulkResults(null);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (result) => {
        if (result.errors && result.errors.length) { setBulkParseError(result.errors[0].message); return; }
        const mapped = (result.data || []).map(r => {
          const fromName = (r['FromName'] || '').trim();
          const first = (r['First Name'] || '').trim();
          const last = (r['Last Name'] || '').trim();
          const display = fromName || [first, last].filter(Boolean).join(' ');
          return {
            email_address: (r['Email'] || '').trim(),
            display_name: display || null,
            signature_html: (r['Signature'] || '').trim() || null,
            smtp_host: (r['SMTP Host'] || '').trim(),
            smtp_port: parseInt(r['SMTP Port']) || 587,
            smtp_username: (r['SMTP Username'] || '').trim() || null,
            smtp_password: (r['SMTP Password'] || '').trim(),
            imap_host: (r['IMAP Host'] || '').trim() || null,
            imap_port: parseInt(r['IMAP Port']) || 993,
            imap_username: (r['IMAP Username'] || '').trim() || null,
            imap_password: (r['IMAP Password'] || '').trim() || null,
            daily_cap: parseInt(r['Daily Sending Limit']) || 50,
            daily_cap_ceiling: parseInt(r['Maximum Sending Limit']) || 500,
            ramp_pct: parseInt(r['Limit Increase Percentage']) || 20,
            warmup_enabled: (r['Warmup Enabled'] || '').trim().toUpperCase() === 'TRUE',
          };
        }).filter(r => r.email_address);
        setBulkRows(mapped);
      },
      error: (err) => setBulkParseError(err.message),
    });
  }

  async function downloadTemplate() {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/mailboxes/smtp/template.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { toast.error('Could not fetch template: ' + r.status); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'smtp-mailboxes-template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  }

  async function runBulkImport() {
    if (!bulkRows.length) return;
    setBulkImporting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/smtp/bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rows: bulkRows }),
      });
      setBulkResults(await res.json());
    } catch (e) { setBulkResults({ ok: false, error: e.message }); }
    finally { setBulkImporting(false); }
  }

  return (
    <div className="crmmc-ovl" onClick={onClose}>
      <div className="crmmc-dialog" onClick={e => e.stopPropagation()}>
        <div className="crmmc-head">
          <h3>Add SMTP / IMAP mailbox</h3>
          <button className="crmmc-x" onClick={onClose}>×</button>
        </div>
        <div className="crmmc-tabs">
          <button className={'crmmc-tab' + (mode === 'single' ? ' crmmc-tab--on' : '')} onClick={() => setMode('single')}>Single mailbox</button>
          <button className={'crmmc-tab' + (mode === 'bulk' ? ' crmmc-tab--on' : '')} onClick={() => setMode('bulk')}>Bulk import (CSV)</button>
        </div>
        <div className="crmmc-body">
          {mode === 'single' ? (
            <>
              <div className="crmmc-grid2">
                <div className="crmmc-f"><label className="crmmc-lbl">Email address</label><input className="crmmc-input" value={emailAddress} onChange={e => setEmailAddress(e.target.value)} placeholder="user@example.com" /></div>
                <div className="crmmc-f"><label className="crmmc-lbl">Display name (optional)</label><input className="crmmc-input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Jane Doe" /></div>
              </div>
              <div className="crmmc-sec">SMTP (sending)</div>
              <div className="crmmc-grid21">
                <div className="crmmc-f"><label className="crmmc-lbl">Host</label><input className="crmmc-input" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.example.com" /></div>
                <div className="crmmc-f"><label className="crmmc-lbl">Port</label><input className="crmmc-input" type="number" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} /></div>
              </div>
              <div className="crmmc-grid2">
                <div className="crmmc-f"><label className="crmmc-lbl">Username (defaults to email)</label><input className="crmmc-input" value={smtpUsername} onChange={e => setSmtpUsername(e.target.value)} /></div>
                <div className="crmmc-f"><label className="crmmc-lbl">Password</label><input className="crmmc-input" type="password" value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} /></div>
              </div>
              <div className="crmmc-sec">IMAP (receiving)</div>
              <div className="crmmc-grid21">
                <div className="crmmc-f"><label className="crmmc-lbl">Host</label><input className="crmmc-input" value={imapHost} onChange={e => setImapHost(e.target.value)} placeholder="imap.example.com" /></div>
                <div className="crmmc-f"><label className="crmmc-lbl">Port</label><input className="crmmc-input" type="number" value={imapPort} onChange={e => setImapPort(e.target.value)} /></div>
              </div>
              <div className="crmmc-grid2">
                <div className="crmmc-f"><label className="crmmc-lbl">Username (defaults to email)</label><input className="crmmc-input" value={imapUsername} onChange={e => setImapUsername(e.target.value)} /></div>
                <div className="crmmc-f"><label className="crmmc-lbl">Password (defaults to SMTP)</label><input className="crmmc-input" type="password" value={imapPassword} onChange={e => setImapPassword(e.target.value)} /></div>
              </div>
              {testResult && (
                <div className={'crmmc-test ' + (testResult.ok ? 'crmmc-test--ok' : 'crmmc-test--err')}>
                  {testResult.ok ? 'Connection test succeeded.' : 'Connection test failed: ' + JSON.stringify(testResult.errors || testResult.error || testResult)}
                </div>
              )}
              <div className="crmmc-foot">
                <button className="crmmc-btn" onClick={onClose}>Cancel</button>
                <button className="crmmc-btn" onClick={runTest} disabled={testing || !smtpHost || !smtpPassword}>{testing ? 'Testing…' : 'Test connection'}</button>
                <button className="crmmc-btn crmmc-btn--primary" onClick={saveSingle} disabled={saving || !testResult || !testResult.ok}>{saving ? 'Saving…' : 'Save mailbox'}</button>
              </div>
            </>
          ) : (
            <>
              <div className="crmmc-note">
                Download the template, fill one row per mailbox, then upload. Each row is tested against the SMTP/IMAP servers before saving.
                <div className="crmmc-tpl"><button className="crmmc-btn crmmc-btn--sm" onClick={downloadTemplate}>Download CSV template</button></div>
              </div>
              <input className="crmmc-file" type="file" accept=".csv,text/csv" onChange={e => { const f = e.target.files && e.target.files[0]; if (f) handleCsvFile(f); }} />
              {bulkParseError && <div className="crmmc-test crmmc-test--err">CSV parse error: {bulkParseError}</div>}
              {bulkRows.length > 0 && !bulkResults && (
                <>
                  <div className="crmmc-note">Parsed <strong>{bulkRows.length}</strong> rows. Review before importing.</div>
                  <div className="crmmc-tblwrap">
                    <table className="crmmc-tbl">
                      <thead><tr><th>Email</th><th>SMTP host</th><th>IMAP host</th><th>Daily cap</th><th>Warmup</th></tr></thead>
                      <tbody>
                        {bulkRows.slice(0, 50).map((r, i) => (
                          <tr key={i}><td>{r.email_address}</td><td className="crmmc-muted">{r.smtp_host}:{r.smtp_port}</td><td className="crmmc-muted">{r.imap_host || '—'}:{r.imap_port}</td><td className="crmmc-muted">{r.daily_cap}</td><td className="crmmc-muted">{r.warmup_enabled ? 'On' : 'Off'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkRows.length > 50 && <div className="crmmc-tblmore">Showing first 50 of {bulkRows.length}.</div>}
                  </div>
                  <div className="crmmc-foot">
                    <button className="crmmc-btn" onClick={onClose}>Cancel</button>
                    <button className="crmmc-btn crmmc-btn--primary" onClick={runBulkImport} disabled={bulkImporting}>{bulkImporting ? `Importing ${bulkRows.length}…` : `Import ${bulkRows.length} mailboxes`}</button>
                  </div>
                </>
              )}
              {bulkResults && (
                <>
                  <div className="crmmc-note">Import complete: <strong className="crmmc-ok">{bulkResults.succeeded || 0} succeeded</strong>{bulkResults.failed > 0 && <>, <strong className="crmmc-err-t">{bulkResults.failed} failed</strong></>}</div>
                  <div className="crmmc-tblwrap">
                    <table className="crmmc-tbl">
                      <thead><tr><th>Email</th><th>Status</th><th>Detail</th></tr></thead>
                      <tbody>
                        {(bulkResults.results || []).map((r, i) => (
                          <tr key={i}><td>{r.email_address}</td><td className={r.status === 'ok' ? 'crmmc-ok' : 'crmmc-err-t'}>{r.status}</td><td className="crmmc-muted">{r.error || (r.mailbox_id ? `id ${r.mailbox_id}` : '')}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="crmmc-foot"><button className="crmmc-btn crmmc-btn--primary" onClick={onImported}>Done</button></div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Zoho Shared modal ─────────────────────────────────────────────────────
export function SharedZohoModal({ onClose, onImported }) {
  const { getToken } = useAuth();
  const [parents, setParents] = useState([]);
  const [parentId, setParentId] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_BASE}/admin/mailboxes`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        const all = Array.isArray(data) ? data : data.mailboxes || [];
        const zohoParents = all.filter(m => m.provider === 'zoho' && !m.parent_mailbox_id);
        setParents(zohoParents);
        if (zohoParents.length) setParentId(String(zohoParents[0].id));
      } catch (e) { /* ignore */ }
    })();
  }, [getToken]);

  function parseLines() {
    return text.split('\n').map(l => l.trim()).filter(Boolean).map(line => {
      const parts = line.split(',');
      const email = (parts[0] || '').trim();
      const name = (parts.slice(1).join(',') || '').trim();
      return { email_address: email, display_name: name || null };
    }).filter(x => x.email_address);
  }

  async function save() {
    const mailboxes = parseLines();
    if (!parentId) { toast.error('Pick a parent licensed mailbox'); return; }
    if (!mailboxes.length) { toast.error('Enter at least one shared mailbox address'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/zoho/shared`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ parent_mailbox_id: parseInt(parentId), mailboxes }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error('Failed: ' + (data.error || res.status)); return; }
      setResults(data);
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="crmmc-ovl" onClick={onClose}>
      <div className="crmmc-dialog crmmc-dialog--narrow" onClick={e => e.stopPropagation()}>
        <div className="crmmc-head">
          <h3>Add Zoho shared mailboxes</h3>
          <button className="crmmc-x" onClick={onClose}>×</button>
        </div>
        <div className="crmmc-body">
          <p className="crmmc-note">Shared mailboxes send via a parent licensed user (send-as) over the Zoho API. Pick the licensed mailbox, then paste one shared address per line as <code>email, Display Name</code>.</p>
          <label className="crmmc-lbl">Parent licensed mailbox</label>
          <select className="crmmc-input crmmc-mb" value={parentId} onChange={e => setParentId(e.target.value)}>
            {parents.length === 0 && <option value="">No Zoho licensed mailbox connected</option>}
            {parents.map(p => <option key={p.id} value={p.id}>{p.email_address}</option>)}
          </select>
          <label className="crmmc-lbl">Shared mailboxes (one per line: email, Display Name)</label>
          <textarea className="crmmc-input crmmc-mono crmmc-mb" value={text} onChange={e => setText(e.target.value)} rows={8} placeholder={"mm@webcitegroup.co.uk, Mitchell Morton\nhc@webcitegroup.co.uk, Harry Clark"} />
          {results && (
            <div className="crmmc-results">
              Imported: {results.succeeded} ok, {results.failed} failed.
              {results.results && results.results.filter(r => r.status !== 'ok').map((r, i) => (
                <div key={i} className="crmmc-err-t">{r.email_address}: {r.error}</div>
              ))}
            </div>
          )}
          <div className="crmmc-foot">
            <button className="crmmc-btn" onClick={onClose}>{results ? 'Close' : 'Cancel'}</button>
            {!results && <button className="crmmc-btn crmmc-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Add mailboxes'}</button>}
            {results && <button className="crmmc-btn crmmc-btn--primary" onClick={onImported}>Done</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bulk edit modal (caps + warmup) ───────────────────────────────────────
export function BulkEditModal({ mailboxIds, onClose, onDone }) {
  const { getToken } = useAuth();
  const [dailyCap, setDailyCap] = useState('');
  const [ceiling, setCeiling] = useState('');
  const [ramp, setRamp] = useState('');
  const [warmup, setWarmup] = useState('');
  const [replyRate, setReplyRate] = useState('');
  const [dailyVolume, setDailyVolume] = useState('');
  const [targetVolume, setTargetVolume] = useState('');
  const [spamSave, setSpamSave] = useState('');
  const [markImportant, setMarkImportant] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    const capFields = {};
    if (dailyCap !== '') capFields.daily_cap = parseInt(dailyCap);
    if (ceiling !== '') capFields.daily_cap_ceiling = parseInt(ceiling);
    if (ramp !== '') capFields.ramp_pct = parseInt(ramp);

    const warmupFields = {};
    if (warmup !== '') warmupFields.enabled = warmup === 'on';
    if (replyRate !== '') warmupFields.reply_rate_pct = parseInt(replyRate);
    if (dailyVolume !== '') warmupFields.daily_volume = parseInt(dailyVolume);
    if (targetVolume !== '') warmupFields.target_volume = parseInt(targetVolume);
    if (ramp !== '') warmupFields.ramp_pct = parseInt(ramp);
    if (spamSave !== '') warmupFields.spam_save_enabled = spamSave === 'on';
    if (markImportant !== '') warmupFields.mark_important_enabled = markImportant === 'on';

    if (Object.keys(capFields).length === 0 && Object.keys(warmupFields).length === 0) { toast.error('Set at least one field'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      if (Object.keys(capFields).length > 0) {
        const r1 = await fetch(`${API_BASE}/mailboxes/bulk-update`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: mailboxIds, fields: capFields }),
        });
        const d1 = await r1.json();
        if (!r1.ok) { toast.error('Caps failed: ' + (d1.error || r1.status)); setSaving(false); return; }
      }
      if (Object.keys(warmupFields).length > 0) {
        const r2 = await fetch(`${API_BASE}/mailboxes/warmup/bulk-update`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ids: mailboxIds, fields: warmupFields }),
        });
        const d2 = await r2.json();
        if (!r2.ok) { toast.error('Warmup failed: ' + (d2.error || r2.status)); setSaving(false); return; }
      }
      toast.success('Applied to ' + mailboxIds.length + ' mailbox' + (mailboxIds.length === 1 ? '' : 'es'));
      onDone();
    } catch (e) { toast.error('Failed: ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="crmmc-ovl" onClick={onClose}>
      <div className="crmmc-dialog" onClick={e => e.stopPropagation()}>
        <div className="crmmc-head">
          <div>
            <h3>Bulk edit</h3>
            <div className="crmmc-subh">{mailboxIds.length} mailbox{mailboxIds.length === 1 ? '' : 'es'} selected · blank fields stay unchanged</div>
          </div>
          <button className="crmmc-x" onClick={onClose}>×</button>
        </div>
        <div className="crmmc-body">
          <div className="crmmc-card">
            <div className="crmmc-card-h">Sending caps</div>
            <div className="crmmc-grid3">
              <div className="crmmc-f"><label className="crmmc-lbl">Daily cap</label><input className="crmmc-input" type="number" value={dailyCap} onChange={e => setDailyCap(e.target.value)} placeholder="unchanged" /></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Daily cap ceiling</label><input className="crmmc-input" type="number" value={ceiling} onChange={e => setCeiling(e.target.value)} placeholder="unchanged" /></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Ramp %</label><input className="crmmc-input" type="number" value={ramp} onChange={e => setRamp(e.target.value)} placeholder="unchanged" /></div>
            </div>
          </div>
          <div className="crmmc-card">
            <div className="crmmc-card-h">Warmup</div>
            <div className="crmmc-grid2">
              <div className="crmmc-f"><label className="crmmc-lbl">Warmup enabled</label><select className="crmmc-input" value={warmup} onChange={e => setWarmup(e.target.value)}><option value="">unchanged</option><option value="on">On</option><option value="off">Off</option></select></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Reply rate %</label><input className="crmmc-input" type="number" value={replyRate} onChange={e => setReplyRate(e.target.value)} placeholder="unchanged" /></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Daily volume</label><input className="crmmc-input" type="number" value={dailyVolume} onChange={e => setDailyVolume(e.target.value)} placeholder="unchanged" /></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Target volume</label><input className="crmmc-input" type="number" value={targetVolume} onChange={e => setTargetVolume(e.target.value)} placeholder="unchanged" /></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Spam-save</label><select className="crmmc-input" value={spamSave} onChange={e => setSpamSave(e.target.value)}><option value="">unchanged</option><option value="on">On</option><option value="off">Off</option></select></div>
              <div className="crmmc-f"><label className="crmmc-lbl">Mark important</label><select className="crmmc-input" value={markImportant} onChange={e => setMarkImportant(e.target.value)}><option value="">unchanged</option><option value="on">On</option><option value="off">Off</option></select></div>
            </div>
          </div>
          <div className="crmmc-foot">
            <button className="crmmc-btn" onClick={onClose}>Cancel</button>
            <button className="crmmc-btn crmmc-btn--primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Apply to ' + mailboxIds.length}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
